import { useEffect } from 'react'
import { useNavigate } from 'react-router'

import { useResetContainerContext } from './ResetContainer'

export const useWorkflowPageScrollReset = (dependencies: unknown[]) => {
    useEffect(() => {
        const container = [...document.querySelectorAll('.workflow-page-content-container')].at(-1)
        if (!container) {
            return
        }
        container.scrollTo({ top: 0, behavior: 'instant' })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dependencies)
}

type Props = {
    stepIndex: number
    canAdvanceToStep2: boolean
    isEditing: boolean
    recordID: string | undefined
    originPathname?: string
}

// This hook handles a couple navigation-related tasks in workflows.
//
// 1. If a user somehow navigates to the preview step directly (say by reloading)
//    then we want to send them back to the first step.
// 2. If a user has finished creating a record and is on the success step and
//    then hits Back, they'll be taken back to the first step. In this case, we
//    want to reset the workflow, as they shouldn't be able to continue editing
//    the record they've already saved. If the user was editing a record instead
//    then they're allowed to go back to the edit step without resetting.
export const useNavigationHandlers = ({
    stepIndex,
    canAdvanceToStep2,
    isEditing,
    recordID,
    originPathname,
}: Props) => {
    const navigate = useNavigate()
    const { onReset } = useResetContainerContext()

    // Handles case (1) above.
    useEffect(() => {
        if (stepIndex === 0 || canAdvanceToStep2) {
            return
        }
        navigate(window.location.pathname)
    }, [stepIndex, canAdvanceToStep2, navigate])

    // Handles case (2) above.
    useEffect(() => {
        if (stepIndex !== 0 || isEditing || recordID === undefined) {
            return
        }

        originPathname !== undefined ? navigate(originPathname) : onReset()
    }, [stepIndex, isEditing, recordID, onReset, navigate, originPathname])

    useWorkflowPageScrollReset([stepIndex])
}
